/**
 * Mock API layer for `?dev=1` (UI preview without backend).
 *
 * Why: `?dev=1` bypasses the Telegram SDK via mock-env.ts so you can
 * preview the app in Chrome without Telegram WebView. But every API
 * call still goes through `authFetch` → real Worker. If the Worker
 * (and the ngrok tunnel it lives behind) isn't running locally, every
 * data-driven page (Library, /generating, /api/me check inside AppGate)
 * fails with `Failed to fetch`. That kills 90% of pages.
 *
 * This module intercepts `authFetch` BEFORE the network call when the
 * dev-preview flag is set, and returns canned data that matches the
 * real API contract. Result: `npm run dev` + open localhost:3002/?dev=1
 * in Chrome gives you the full app with realistic-looking data, no
 * ngrok / Worker / Postgres needed.
 *
 * Trade-offs (acknowledged, NOT addressed here):
 *   - Pay flow won't complete end-to-end — `openInvoice` from the
 *     Telegram SDK is mocked separately in mock-env and can't honor
 *     a fake invoice URL. POST /api/generations returns a fake id +
 *     placeholder invoice_url; clicking Pay will fail at SDK level.
 *     For payment-flow testing use the real backend via `npm run dev:full`.
 *   - Photo uploads to /api/generations are silently dropped (we don't
 *     persist the multipart File anywhere). The mock just returns OK
 *     so the wizard can proceed.
 *
 * Keep the mock data SHAPES in lock-step with the real API contract in
 * lib/api.ts. Field names, optional-ness, and types must match what the
 * Worker emits — otherwise the UI breaks in dev-preview-only and we
 * miss it until production.
 */

import type {
  User,
  Generation,
  CreateGenerationResult,
  ListGenerationsResult,
  Model,
} from './api';

/** True when the page was opened with `?dev=1` in the URL. Module-level
 *  constant — checked once at boot. Toggling the flag mid-session
 *  requires a reload (acceptable for a dev affordance). */
export const IS_DEV_PREVIEW =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('dev') === '1';

// ────────────────────────────────────────────────────────────────────
// Mock data
// ────────────────────────────────────────────────────────────────────

const MOCK_USER: User = {
  tg_id: 999999999,
  username: 'dev_preview',
  first_name: 'Dev',
  last_name: 'Preview',
  language_code: 'en',
  is_premium: false,
  created_at: new Date(Date.now() - 7 * 86_400_000).toISOString(),
  updated_at: new Date().toISOString(),
};

const MOCK_MODELS: Model[] = [
  { id: 'm-mira',  name: 'Mira',  photo_key: 'mock/mira.jpg',  tags: ['popular', 'brunette'] },
  { id: 'm-luna',  name: 'Luna',  photo_key: 'mock/luna.jpg',  tags: ['popular', 'blonde'] },
  { id: 'm-aria',  name: 'Aria',  photo_key: 'mock/aria.jpg',  tags: ['new'] },
  { id: 'm-nova',  name: 'Nova',  photo_key: 'mock/nova.jpg',  tags: ['new', 'redhead'] },
  { id: 'm-iris',  name: 'Iris',  photo_key: 'mock/iris.jpg',  tags: [] },
];

// Public test video (Big Buck Bunny, ~10s sample from Google's CDN).
// Used as output_video_url for the "done" mock generations so the
// ResultPage video player has something to render. Cross-origin but
// permitted: the <video> tag doesn't need CORS for playback.
const MOCK_VIDEO_URL =
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';

const MOCK_GENERATIONS_HISTORY: Generation[] = [
  // Most recent — succeeded
  {
    id: 'gen-mock-1',
    status: 'done',
    kiss_type: 'lips',
    duration_sec: 10,
    scene: 'cozy-cafe',
    output_video: 'mock/gen-1.mp4',
    output_video_url: MOCK_VIDEO_URL,
    error_message: null,
    created_at: new Date(Date.now() - 2 * 3_600_000).toISOString(),
    completed_at: new Date(Date.now() - 2 * 3_600_000 + 180_000).toISOString(),
  },
  // Yesterday — also done
  {
    id: 'gen-mock-2',
    status: 'done',
    kiss_type: 'french',
    duration_sec: 15,
    scene: 'sunset-beach',
    output_video: 'mock/gen-2.mp4',
    output_video_url: MOCK_VIDEO_URL,
    error_message: null,
    created_at: new Date(Date.now() - 1 * 86_400_000).toISOString(),
    completed_at: new Date(Date.now() - 1 * 86_400_000 + 220_000).toISOString(),
  },
  // 3 days ago — failed (so dev sees the "couldn't finish" tile state)
  {
    id: 'gen-mock-3',
    status: 'failed',
    kiss_type: 'cheek',
    duration_sec: 6,
    scene: 'park-spring',
    output_video: null,
    output_video_url: null,
    error_message: 'no face detected in photo_1',
    created_at: new Date(Date.now() - 3 * 86_400_000).toISOString(),
    completed_at: new Date(Date.now() - 3 * 86_400_000 + 90_000).toISOString(),
  },
];

// ────────────────────────────────────────────────────────────────────
// /generating page simulation
// ────────────────────────────────────────────────────────────────────
//
// /generating polls GET /:id every 4s. For an honest preview of the
// 2-phase UI we cycle through states based on how long ago the id was
// first requested. Total simulated pipeline: ~16s.
//
// Timeline:
//   0-4s   → status='pending', pair_image_key=null (kickoff)
//   4-9s   → status='processing', image_attempts=1, no pair_image_key
//            (image phase active)
//   9-13s  → status='processing', pair_image_key set, video_attempts=1
//            (image done → video phase active)
//   >13s   → status='done', output_video_url set
//
// Map is in-memory — refreshing the page resets all simulations to
// pending. That's fine: dev preview is for one-shot UX checking, not
// long-running sessions.
const generationFirstSeen = new Map<string, number>();

function simulateGenerationProgress(id: string): Generation {
  if (!generationFirstSeen.has(id)) generationFirstSeen.set(id, Date.now());
  const ageSec = (Date.now() - generationFirstSeen.get(id)!) / 1000;

  const base: Generation = {
    id,
    status: 'pending',
    kiss_type: 'lips',
    duration_sec: 10,
    scene: 'cozy-cafe',
    output_video: null,
    output_video_url: null,
    pair_image_key: null,
    pair_image_url: null,
    image_attempts: 0,
    video_attempts: 0,
    error_message: null,
    created_at: new Date(generationFirstSeen.get(id)!).toISOString(),
    completed_at: null,
  };

  if (ageSec < 4) return base; // pending kickoff

  if (ageSec < 9) {
    return { ...base, status: 'processing', image_attempts: 1 };
  }

  if (ageSec < 13) {
    return {
      ...base,
      status: 'processing',
      pair_image_key: 'mock/pair-preview.jpg',
      // Public sample image so PairThumbHero has something to render.
      pair_image_url: MOCK_VIDEO_URL, // browser will fail to decode as image; ok — gradient fallback kicks in
      image_attempts: 1,
      video_attempts: 1,
    };
  }

  return {
    ...base,
    status: 'done',
    pair_image_key: 'mock/pair-preview.jpg',
    pair_image_url: null,
    image_attempts: 1,
    video_attempts: 1,
    output_video: `mock/${id}.mp4`,
    output_video_url: MOCK_VIDEO_URL,
    completed_at: new Date().toISOString(),
  };
}

// ────────────────────────────────────────────────────────────────────
// Router — match path → return Response
// ────────────────────────────────────────────────────────────────────

/**
 * Return a canned Response for the given (path, init), matching the
 * shape the real Worker would return. Throw RouteNotMocked if path
 * doesn't match anything — caller can fall back to real fetch in that
 * case (so adding a new endpoint without updating mocks degrades to
 * "network error in dev preview" rather than silent wrong data).
 */
export function mockApiResponse(path: string, init?: RequestInit): Response {
  const method = (init?.method ?? 'GET').toUpperCase();
  // path may include query string — split off for routing.
  const [pathname, query = ''] = path.split('?', 2);

  // GET /api/me
  if (method === 'GET' && pathname === '/api/me') {
    return jsonResponse(MOCK_USER);
  }

  // GET /api/catalog
  if (method === 'GET' && pathname === '/api/catalog') {
    return jsonResponse({ models: MOCK_MODELS });
  }

  // GET /api/generations (list) — query param `limit` accepted, ignored
  // because we always return the same fake history.
  if (method === 'GET' && pathname === '/api/generations') {
    const result: ListGenerationsResult = {
      items: MOCK_GENERATIONS_HISTORY,
      has_more: false,
    };
    return jsonResponse(result);
  }

  // GET /api/generations/:id — driven by the progress simulator above.
  // Includes the static history items if id matches one of those (so
  // tapping Library → Done kiss → ResultPage works).
  const genIdMatch = pathname.match(/^\/api\/generations\/([\w-]+)$/);
  if (method === 'GET' && genIdMatch) {
    const id = genIdMatch[1];
    const fromHistory = MOCK_GENERATIONS_HISTORY.find((g) => g.id === id);
    if (fromHistory) {
      // For history items return as-is; simulation only applies to
      // fresh ids minted by POST /api/generations.
      return jsonResponse(fromHistory);
    }
    return jsonResponse(simulateGenerationProgress(id));
  }

  // POST /api/generations (create) — return fake invoice. Caller will
  // then call openInvoice() in the Telegram SDK, which is mocked in
  // mock-env.ts. End-to-end Pay flow won't complete in dev preview;
  // for that, use the full backend (`npm run dev:full`).
  if (method === 'POST' && pathname === '/api/generations') {
    const fakeId = `gen-mock-${Date.now()}`;
    const result: CreateGenerationResult = {
      id: fakeId,
      status: 'pending',
      kiss_type: 'lips',
      duration_sec: 10,
      scene: 'cozy-cafe',
      cost_stars: 1,
      // Placeholder URL — openInvoice can't actually consume this in
      // dev preview. DurationStep will get an error from the SDK and
      // surface it inline; user clicks back and continues navigating.
      invoice_url: 'tg://invoice/mock_dev_preview',
    };
    return jsonResponse(result);
  }

  console.warn('[dev-preview-api] unmocked', { method, pathname, query });
  // Fail with a clearly-tagged 501 so the caller's existing error path
  // surfaces in UI rather than silent wrong data. New endpoints should
  // either be added to this mock or accept that they don't work in
  // ?dev=1 mode.
  return jsonResponse(
    { error: 'dev_preview_not_mocked', detail: `${method} ${pathname} has no dev-preview mock` },
    501,
  );
}

function jsonResponse(data: unknown, status = 200): Response {
  return new Response(JSON.stringify(data), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}
